import type { PropsWithChildren } from 'react';
import dynamic from 'next/dynamic';

import SanityPage from './SanityPage';
import { SanityPageProps } from './withSanityPageProps';
import { PortableText, Serializers, SerializerContext } from './serializers';
import { SanityAnalyticsProvider, SanityTrackedBlock } from './analytics';

// this is to avoid SSR issues
const PreviewPageBuilder = dynamic(() => import('./PreviewPageBuilder'), {
  ssr: false,
});

const blockSerializers = { container: ({ children }: PropsWithChildren) => <>{children}</> };

export type PageBuilderProps = SanityPageProps & {
  serializers?: Serializers;
};

/**
 * Represents a Sanity backed page that makes use of the PortableText based page
 * builder functionality.
 *
 * Each page-builder block is rendered inside a `SanityTrackedBlock`, which takes the
 * place of the portable-text container element so DOM depth around a block is unchanged.
 * Preview mode renders without block tracking.
 *
 * In next-sanity v5, preview functionality is handled by a separate client-only component
 * to avoid SSR/hydration issues with LiveQueryProvider.
 */
export default function PageBuilder({ data, preview, token, serializers }: PageBuilderProps) {
  const serializerOptions = {
    serializers,
    data: data.static,
  };

  // Non-preview mode: render normally (server-side compatible)
  if (!preview) {
    const blocks = data.page?.pageBuilder ?? [];
    const pageData = {
      id: data.page._id,
      slug: data.page.slug.current,
      title: data.page.pageName,
      blockCount: data.page.pageBuilder.length,
    };

    return (
      <SanityPage data={data} preview={false}>
        <SanityAnalyticsProvider key={pageData.id} page={pageData}>
          <SerializerContext.Provider value={serializerOptions}>
            {blocks.map((block, index) => (
              <SanityTrackedBlock key={block._key} block={block} index={index}>
                <PortableText content={[block]} serializers={blockSerializers} />
              </SanityTrackedBlock>
            ))}
          </SerializerContext.Provider>
        </SanityAnalyticsProvider>
      </SanityPage>
    );
  }

  // Preview mode: use client-only component
  return <PreviewPageBuilder data={data} preview={preview} token={token} serializers={serializers} />;
}
